import { getOrdersQuery } from '@/api/orders/orderQueries';
import type { OrderFilters as OrderFiltersType } from '@/api/orders/orderTypes';
import { TableCell, TableHead, TableRow } from '@/components/common/Table';
import type { Order, OrderState } from '@/domain/Order';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/utils/dateUtils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { OrderDetails } from './OrderDetails';
import { OrderFilters } from './OrderFilters';

const getStatusDisplay = (
  state: OrderState,
): { label: string; color: string } => {
  const statusMap: Record<string, { label: string; color: string }> = {
    New: { label: 'New', color: 'text-blue-600' },
    Waiting: { label: 'Waiting', color: 'text-yellow-600' },
    Approved: { label: 'Approved', color: 'text-green-600' },
    Rejected: { label: 'Rejected', color: 'text-red-600' },
    Shipped: { label: 'Shipped', color: 'text-purple-600' },
    'In terminal': { label: 'In terminal', color: 'text-orange-600' },
    Delivered: { label: 'Delivered', color: 'text-green-700' },
    Failed: { label: 'Failed', color: 'text-red-700' },
  };
  return (
    statusMap[state as string] ?? {
      label: String(state),
      color: 'text-slate-600',
    }
  );
};

const INITIAL_ORDER_FILTERS: OrderFiltersType = {};

export const OrdersRow = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<OrderFiltersType>(
    INITIAL_ORDER_FILTERS,
  );
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_ORDER_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const { data: response, isLoading } = useQuery(getOrdersQuery(page, filters));
  const rawOrders = response?.data || [];
  const counts = response?.counts;
  const meta = response?.meta;

  const orders = filters.is_overdue_paid
    ? rawOrders.filter((order) => order.is_overdue_paid)
    : rawOrders;

  const handleExpandToggle = (orderId: number) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  return (
    <DashboardTableAccordion
      value={status ? 'Waiting carts' : 'Orders'}
      title={status ? 'Waiting carts' : 'Orders'}
      accordionOpenValue={accordionOpenValue}
      onOpen={handleAccordionOpen}
      data={orders}
      isLoading={isLoading}
      emptyMessage="No orders found."
      loadingMessage="Loading orders..."
      paginationMeta={meta}
      onPageChange={setPage}
      multiRow={true}
      summarySection={
        !status ? (
          <div className="flex justify-between text-base">
            <span className="text-table-foreground">
              <span className="font-light">
                Not Started {counts?.not_started || 0}
              </span>
            </span>
            <span className="text-table-foreground">
              <span className="font-light">
                In progress {counts?.in_progress || 0}
              </span>
            </span>
            <span className="text-table-foreground">
              <span className="font-light">Done {counts?.done || 0}</span>
            </span>
          </div>
        ) : null
      }
      filtersComponent={
        <OrderFilters
          key={filtersResetKey}
          filters={filters}
          onFiltersChange={(newFilters) => {
            setFilters(newFilters);
            setPage(1);
          }}
        />
      }
      renderHeader={() => (
        <>
          <TableHead>Order</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>L.T. int</TableHead>
          <TableHead>L.T. ext</TableHead>
          <TableHead>LOT</TableHead>
          <TableHead className="w-24"></TableHead>
        </>
      )}
      renderRow={(order: Order) => {
        const status = getStatusDisplay(order.state);
        const isExpanded = expandedOrderId === order.id;

        return (
          <>
            <TableRow className={cn(order.is_child && 'bg-gray-100')}>
              <TableCell>
                {order.user?.name} {order.user?.surname}
              </TableCell>
              <TableCell>{formatDate(order.created_at)}</TableCell>
              <TableCell>
                <span className={cn('font-medium', status.color)}>
                  {status.label}
                </span>
                {order.state === 'Paid' && order.is_overdue_paid && (
                  <span>🚩</span>
                )}
              </TableCell>
              <TableCell>
                {order.lead_time_internal
                  ? `${order.lead_time_internal.days}d ${order.lead_time_internal.hours}h`
                  : '-'}
              </TableCell>
              <TableCell>
                {order.lead_time_external
                  ? `${order.lead_time_external.days}d ${order.lead_time_external.hours}h`
                  : '-'}
              </TableCell>
              <TableCell>{order.lot_number || '-'}</TableCell>
              <TableCell className="text-right">
                <button
                  className="text-primary font-medium hover:underline"
                  onClick={() => handleExpandToggle(order.id)}
                >
                  {isExpanded ? 'Hide' : 'Expand'}
                </button>
              </TableCell>
            </TableRow>
            {isExpanded && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="p-0">
                  <div className="bg-background px-4 pb-6">
                    <OrderDetails order={order} />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </>
        );
      }}
    />
  );
};
